import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Inventory as InventoryIcon,
  Inventory2 as Inventory2Icon,
  Person as PersonIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate, useParams } from 'react-router-dom';
import { getSharedInventoryItems } from '../services/inventorySharingService';

interface InventoryItem {
  id: string;
  itemName: string;
  description?: string;
  photoUrl?: string;
  quantity?: number;
  category?: string;
  brand?: string;
  store?: string;
}

export const SharedInventoryPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ownerId } = useParams<{ ownerId: string }>();

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  useEffect(() => {
    if (ownerId && user?.uid) {
      loadItems();
    }
  }, [ownerId, user?.uid]);

  const loadItems = async () => {
    if (!ownerId || !user?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getSharedInventoryItems(ownerId, user.uid);
      setItems(data);

      // Pegar nome do owner do primeiro item ou da resposta
      if (data.length > 0) {
        // Buscar nome do owner se disponível
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar acervo');
    } finally {
      setLoading(false);
    }
  };

  // Obter categorias únicas
  const categories = ['all', ...Array.from(new Set(items.map(item => item.category).filter(Boolean)))];

  // Filtrar itens
  let displayItems = searchTerm.trim()
    ? items.filter(item =>
        item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;

  if (selectedCategory !== 'all') {
    displayItems = displayItems.filter(item => item.category === selectedCategory);
  }

  if (loading) {
    return (
      <>
        <TopBar
          title="Acervo Compartilhado"
          showBackButton
          onBackClick={() => navigate('/inventory')}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress sx={{ color: theme.colors.primary }} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar
          title="Acervo Compartilhado"
          showBackButton
          onBackClick={() => navigate('/inventory')}
        />
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 3
        }}>
          <Typography sx={{ color: theme.colors.text.secondary, textAlign: 'center' }}>
            {error}
          </Typography>
        </Box>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <TopBar
        title="Acervo Compartilhado"
        icon={<PersonIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton
        showProfileButton
        onBackClick={() => navigate('/inventory')}
      />

      <Box sx={{
        paddingTop: '70px',
        paddingBottom: '120px',
        maxWidth: '1200px',
        margin: '0 auto',
        px: 3,
        minHeight: '100vh',
      }}>
        {/* Info Banner */}
        <Box
          sx={{
            backgroundColor: `${theme.colors.primary}15`,
            borderRadius: theme.radii.medium,
            p: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <PersonIcon sx={{ color: theme.colors.primary }} />
          <Typography variant="body2" sx={{ color: theme.colors.text.primary }}>
            Você está visualizando o acervo de outro usuário (somente leitura)
          </Typography>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Buscar itens..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: theme.radii.medium,
              backgroundColor: theme.colors.card,
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.colors.text.secondary }} />
                </InputAdornment>
              ),
            }
          }}
        />

        {/* Category Filter */}
        {categories.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                  backgroundColor: theme.colors.card,
                }
              }}
            >
              <InputLabel shrink>Filtrar por categoria</InputLabel>
              <Select
                value={selectedCategory}
                label="Filtrar por categoria"
                onChange={(e) => setSelectedCategory(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterIcon sx={{ color: theme.colors.text.secondary, ml: 1 }} />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">
                  <em>Todas as categorias</em>
                </MenuItem>
                {categories.filter(cat => cat !== 'all').sort().map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCategory !== 'all' && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={selectedCategory}
                  onDelete={() => setSelectedCategory('all')}
                  color="primary"
                  size="small"
                  sx={{
                    backgroundColor: theme.colors.primary,
                    color: 'white',
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Stats + View Toggle */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: theme.colors.text.secondary }}>
            {displayItems.length} {displayItems.length === 1 ? 'item' : 'itens'}
          </Typography>
          {displayItems.length > 0 && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text.secondary,
                  padding: '4px 8px',
                  '&.Mui-selected': {
                    backgroundColor: `${theme.colors.primary}20`,
                    color: theme.colors.primary,
                    '&:hover': {
                      backgroundColor: `${theme.colors.primary}30`,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="grid view">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* Items */}
        {displayItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <InventoryIcon sx={{ fontSize: 48, color: theme.colors.text.secondary, mb: 2 }} />
            <Typography sx={{ color: theme.colors.text.secondary }}>
              {searchTerm ? `Nenhum item encontrado para "${searchTerm}"` : 'Este acervo está vazio'}
            </Typography>
          </Box>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <Grid container spacing={2}>
            {displayItems.map((item) => (
              <Grid key={item.id} size={{ xs: 6, sm: 4, md: 3 }}>
                <Card
                  sx={{
                    borderRadius: theme.radii.medium,
                    boxShadow: 'none',
                    backgroundColor: theme.colors.card,
                    border: `1px solid ${theme.colors.border}`,
                    overflow: 'hidden',
                  }}
                >
                  {/* Item photo */}
                  {item.photoUrl ? (
                    <CardMedia
                      component="img"
                      image={item.photoUrl}
                      alt={item.itemName}
                      sx={{
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        aspectRatio: '1 / 1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                      }}
                    >
                      <Inventory2Icon sx={{ fontSize: '3rem', opacity: 0.4, color: theme.colors.text.secondary }} />
                    </Box>
                  )}

                  {/* Item info */}
                  <Box sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: theme.colors.text.primary,
                        fontSize: '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      }}
                    >
                      {item.itemName}
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.colors.text.secondary,
                        fontSize: '0.7rem',
                        textAlign: 'center',
                      }}
                    >
                      Qtd: {item.quantity || 1}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* List View */
          <Grid container spacing={2}>
            {displayItems.map((item) => (
              <Grid key={item.id} size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    display: 'flex',
                    height: 100,
                    backgroundColor: theme.colors.card,
                    borderRadius: theme.radii.medium,
                    border: `1px solid ${theme.colors.border}`,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flex: 1,
                      px: 2,
                      minWidth: 0,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        flexShrink: 0,
                        borderRadius: theme.radii.small,
                        backgroundImage: item.photoUrl
                          ? `url(${item.photoUrl})`
                          : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {!item.photoUrl && (
                        <InventoryIcon sx={{ fontSize: '2.2rem', opacity: 0.5, color: theme.colors.text.secondary }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: theme.colors.text.primary,
                          fontSize: '1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.itemName}
                      </Typography>
                      <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                        Qtd: {item.quantity || 1}
                        {item.category && ` • ${item.category}`}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <BottomNavigation />
    </>
  );
};
